<template>
    <v-container fluid>
        <!-- Freitext -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('Suchen') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field
                    v-model="searchParams.query"
                    dense
                    outlined
                    hide-details="auto"
                    :placeholder="$t('freitext_search')"
                />
            </v-col>
        </v-row>
        <!-- Status -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('status') }}</label>
            </v-col>
            <v-col cols="9">
                <v-select
                    v-model="searchParams.status"
                    dense
                    outlined
                    :items="statusItems"
                    hide-details="auto"
                    multiple
                    :placeholder="$t('choose_multi')"
                    clearable
                >
                    <template #selection="{ item, index }">
                        <span v-if="index > 0">, </span><span>{{ item.text }}</span>
                    </template>
                </v-select>
            </v-col>
        </v-row>
        <!-- GenreGroups, aka Rubriken -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('rubriken') }}</label>
            </v-col>
            <v-col cols="9">
                <GenreGroupSelect
                    v-model="searchParams.genre_group_ids"
                    hide-details="auto"
                    :page-id="1"
                    clearable
                    :placeholder="$t('choose_multi')"
                    multiple
                />
            </v-col>
        </v-row>

        <!-- Tags -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('tags') }}</label>
            </v-col>
            <v-col cols="9">
                <TagSelect
                    v-model="searchParams.tag_ids"
                    hide-details="auto"
                    for-event
                    for-schedule
                    clearable
                    show-download
                    :placeholder="$t('choose_multi')"
                />
            </v-col>
        </v-row>

        <!-- Target Audience -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('target_audience') }}</label>
            </v-col>
            <v-col cols="9">
                <TargetAudienceSelect
                    v-model="searchParams.target_audience_ids"
                    hide-details="auto"
                    clearable
                    :placeholder="$t('choose_multi')"
                    multiple
                />
            </v-col>
        </v-row>

        <!-- Region -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('region') }}</label>
            </v-col>
            <v-col cols="9">
                <RegionSelect
                    v-model="searchParams.region_id"
                    hide-details="auto"
                    :placeholder="$t('choose_multi')"
                    clearable
                />
            </v-col>
        </v-row>

        <!-- Ausserhalb -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('kanton') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    :value="searchParams.region || null"
                    row
                    class="mt-0"
                    hide-details="auto"
                    @change="searchParams.region = $event"
                >
                    <v-radio :value="null" :label="$t('egal')" />
                    <v-radio value="tg" :label="$t('tg')" />
                    <v-radio value="extern" :label="$t('ausserhalb')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Ort -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="b-2 tgk-paragraph-text">{{ $t('ort') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field
                    v-model="searchParams.ort"
                    input-class="mr-1"
                    hide-details="auto"
                    dense
                    outlined
                    clearable
                    :placeholder="$t('plz_ort')"
                />
            </v-col>
        </v-row>

        <!-- Datum -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('datum') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <DateTextfield
                    v-model="searchParams.event_date_from"
                    input-class="mr-1"
                    :placeholder="$t('von')"
                    hide-details="auto"
                />
                <DateTextfield v-model="searchParams.event_date_to" :placeholder="$t('bis')" hide-details="auto" />
            </v-col>
        </v-row>

        <!-- erstellt -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('erstellt') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <DateTextfield
                    v-model="searchParams.created_date_from"
                    input-class="mr-1"
                    :placeholder="$t('von')"
                    hide-details="auto"
                />
                <DateTextfield v-model="searchParams.created_date_to" :placeholder="$t('bis')" hide-details="auto" />
            </v-col>
        </v-row>

        <!-- bearbeitet -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('bearbeitet') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <DateTextfield
                    v-model="searchParams.modified_date_from"
                    input-class="mr-1"
                    :placeholder="$t('von')"
                    hide-details="auto"
                />
                <DateTextfield v-model="searchParams.modified_date_to" :placeholder="$t('bis')" hide-details="auto" />
            </v-col>
        </v-row>

        <!-- publiziert -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('publiziert') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <DateTextfield
                    v-model="searchParams.published_from"
                    input-class="mr-1"
                    :placeholder="$t('von')"
                    hide-details="auto"
                />
                <DateTextfield v-model="searchParams.published_to" :placeholder="$t('bis')" hide-details="auto" />
            </v-col>
        </v-row>

        <!-- getippt -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-2">
                <label class="tgk-paragraph-text">{{ $t('featured') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <v-checkbox v-model="searchParams.is_featured" class="mt-0" hide-details="auto" />
                <span class="tgk-paragraph-text mt-2">{{ $t('featured_events_hint') }}</span>
            </v-col>
        </v-row>

        <!-- Newsletter -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-2">
                <label class="tgk-paragraph-text">{{ $t('nl') }}</label>
            </v-col>
            <v-col cols="9" class="d-flex">
                <v-checkbox v-model="searchParams.to_newsletter" class="mt-0" hide-details="auto" />
                <span class="tgk-paragraph-text mt-2">{{ $t('nl_hint_event') }}</span>
            </v-col>
        </v-row>

        <!-- Klasse -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-top mt-2">
                <label class="tgk-paragraph-text">{{ $t('event_class') }}</label>
            </v-col>
            <v-col cols="9">
                <v-select
                    v-model="searchParams.event_class"
                    multiple
                    outlined
                    dense
                    hide-details="auto"
                    :items="[
                        { value: Event.CLASS_EVENT, text: $t('events') },
                        { value: Event.CLASS_CINEMA, text: $t('kino') },
                        { value: Event.CLASS_EXHIBITION, text: $t('ausstellungen') },
                        { value: Event.CLASS_STREAM, text: $t('stream') },
                        { value: Event.CLASS_PERMANENT_EXHIBITION, text: $t('permanent_exhibitions') },
                        { value: Event.CLASS_FESTIVAL, text: $t('festival') },
                        { value: Event.CLASS_TOURNEE, text: $t('tournee') },
                    ]"
                >
                </v-select>
                <v-radio-group
                    :value="searchParams.event_class || null"
                    class="mt-0"
                    hide-details="auto"
                    @change="searchParams.event_class = $event"
                >
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- event_mode -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('event_mode') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    :value="searchParams.event_mode || null"
                    row
                    class="mt-0"
                    hide-details="auto"
                    @change="searchParams.event_mode = $event"
                >
                    <v-radio :value="null" :label="$t('egal')" />
                    <v-radio :value="Event.MODE_MIXED" :label="$t('mode_mixed')" />
                    <v-radio :value="Event.MODE_ONLINE" :label="$t('mode_online')" />
                    <v-radio :value="Event.MODE_OFFLINE" :label="$t('mode_offline')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Barrierefrei -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('barrierefrei') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    :value="searchParams.barrierefrei || null"
                    row
                    class="mt-0"
                    hide-details="auto"
                    @change="searchParams.barrierefrei = $event"
                >
                    <v-radio :value="null" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('yes')" />
                    <v-radio value="false" :label="$t('no')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- linked in magazin -->
        <v-row dense>
            <v-col cols="3" class="d-flex align-center">
                <label class="tgk-paragraph-text">{{ $t('linked_in_magazin') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    :value="searchParams.linked_in_magazin || null"
                    row
                    class="mt-0"
                    hide-details="auto"
                    @change="searchParams.linked_in_magazin = $event"
                >
                    <v-radio :value="null" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('yes')" />
                    <v-radio value="false" :label="$t('no')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Import-Schutz -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('import_protection') }}</label>
            </v-col>
            <v-col cols="9">
                <v-radio-group
                    :value="searchParams.import_protection || null"
                    row
                    class="mt-0"
                    hide-details="auto"
                    @change="onImportProtectionChange"
                >
                    <v-radio :value="null" :label="$t('egal')" />
                    <v-radio value="true" :label="$t('yes')" />
                    <v-radio value="false" :label="$t('no')" />
                </v-radio-group>
            </v-col>
        </v-row>

        <!-- Herkunft -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('import_origin') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field
                    v-model="searchParams.bp_import_origin"
                    outlined
                    dense
                    placeholder="Import-Identifikation, z.B. 'guidle', 'eventfrog'"
                />
            </v-col>
        </v-row>

        <!-- Import-Typ -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('import_type') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field
                    v-model="searchParams.bp_import_type"
                    outlined
                    dense
                    placeholder="Import-Typ, z.B. bei Guidle: 'small', 'large'"
                />
            </v-col>
        </v-row>

        <!-- Event-Organizer -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('event_organizer') }}</label>
            </v-col>
            <v-col cols="9">
                <EventOrganizerSelect
                    :event="dummyEvent"
                    :show-edit="false"
                    @selected="onEventOrganizerSelect($event)"
                />
            </v-col>
        </v-row>

        <!-- Location -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('location') }}</label>
            </v-col>
            <v-col cols="9">
                <LocationSelectField
                    :show-new="false"
                    :show-edit="false"
                    :location-id="dummyEvent.location_id"
                    :organizer-id="dummyEvent.organizer_id"
                    @selected="onEventLocationSelect($event)"
                />
            </v-col>
        </v-row>

        <!-- Event-Organizer -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('organizer_group') }}</label>
            </v-col>
            <v-col cols="9">
                <OrganizerGroupSelectField
                    :show-new="false"
                    :show-edit="false"
                    :value="dummyEvent.organizer_group_id"
                    :display-value="organizerGroupDisplayValue"
                    :collection="organizerGroupCollection"
                    @selected="onOrganizerGroupSelect($event)"
                />
            </v-col>
        </v-row>

        <!-- ID -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('id') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field v-model="searchParams.id" type="number" dense outlined hide-details="auto" />
            </v-col>
        </v-row>

        <!-- UUID -->
        <v-row dense>
            <v-col cols="3" class="d-flex mt-3">
                <label class="tgk-paragraph-text">{{ $t('uuid') }}</label>
            </v-col>
            <v-col cols="9">
                <v-text-field v-model="searchParams.uuid" dense outlined hide-details="auto" />
            </v-col>
        </v-row>

        <v-row dense>
            <v-col class="d-flex justify-end mt-3">
                <PlainBtn @click="onResetBtn">{{ $t('reset_search') }}</PlainBtn>
                <ActionBtn class="ml-1" @click="onSearchBtn">
                    <KpIcon icon-cls="searchmagnifier" color="white" left />
                    {{ $t('search') }}
                </ActionBtn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import GenreGroupSelect from '@/components/genre_group/select.vue';
import RegionSelect from '@/components/region/select.vue';
import TargetAudienceSelect from '@/components/target_audience/select.vue';
import TagSelect from '@/components/tag/select.vue';
import { Event } from '@/models/Event';
import EventOrganizerSelect from '@/components/event/EventOrganizerSelect.vue';
import LocationSelectField from '@/components/location/SelectField.vue';
import OrganizerGroupSelectField from '@/components/organizer_group/SelectField.vue';
import { OrganizerGroupCollection } from '@/models/OrganizerGroup';

/**
 * Enhanced Search Component for Event search
 *
 * Emits:
 * - search(params): when the user click on "search"
 * - reset: when the user clicks on "reset"
 *
 * The initial search params can be set via initial-params property.
 */
export default {
    name: 'EventEnhancedSearch',

    components: {
        GenreGroupSelect,
        RegionSelect,
        TagSelect,
        TargetAudienceSelect,
        EventOrganizerSelect,
        LocationSelectField,
        OrganizerGroupSelectField,
    },

    props: {
        // the search params, emitted as 'input' on search btn click or reset
        initialParams: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            searchParams: { ...this.initialParams },
            dummyEvent: new Event({
                organizer_id: this.initialParams?.organizer_id,
                event_organizer: this.initialParams?.event_organizer,
                location_id: this.initialParams?.location_id,
                organizer_group_id: this.initialParams?.organizer_group_id,
            }),
            statusItems: [
                { value: Event.STATUS_RELEASED, text: String(this.$t(Event.STATUS_RELEASED)) },
                { value: Event.STATUS_DRAFT, text: String(this.$t(Event.STATUS_DRAFT)) },
                { value: Event.STATUS_REGISTERED, text: String(this.$t(Event.STATUS_REGISTERED)) },
                { value: Event.STATUS_REJECTED, text: String(this.$t(Event.STATUS_REJECTED)) },
                { value: Event.STATUS_DELETED, text: String(this.$t(Event.STATUS_DELETED)) },
                { value: Event.STATUS_IMPORTED, text: String(this.$t(Event.STATUS_IMPORTED)) },
            ],
            organizerGroupCollection: new OrganizerGroupCollection(),
            organizerGroupDisplayValue: this.initialParams?.organizer_group_title || null,
        };
    },

    beforeCreate() {
        this.Event = Event;
    },

    methods: {
        getSearchParams() {
            return { ...this.searchParams };
        },

        onSearchBtn() {
            this.emitChangedParams();
        },

        onResetBtn() {
            this.searchParams = {};
            this.organizerGroupDisplayValue = null;
            this.$emit('reset', {});
        },

        emitChangedParams() {
            this.$emit('search', { ...this.searchParams });
        },

        onEventOrganizerSelect(event) {
            if (event) {
                this.searchParams.organizer_id = event.getId();
                this.searchParams.event_organizer = event.displayTitle;
                this.dummyEvent.organizer_id = event.getId();
                this.dummyEvent.event_organizer = event.displayTitle;
            } else {
                this.searchParams.organizer_id = null;
                this.searchParams.event_organizer = null;
                this.dummyEvent.organizer_id = null;
                this.dummyEvent.event_organizer = null;
            }
        },

        onEventLocationSelect(event) {
            if (event) {
                this.searchParams.location_id = event.getId();
                this.dummyEvent.location_id = event.getId();
            } else {
                this.searchParams.location_id = null;
                this.dummyEvent.location_id = null;
            }
        },

        onOrganizerGroupSelect(organizerGroup) {
            if (organizerGroup) {
                this.searchParams.organizer_group_id = organizerGroup.id;
                this.dummyEvent.organizer_group_id = organizerGroup.id;
                this.searchParams.organizer_group_title = organizerGroup.title;
                this.organizerGroupDisplayValue = organizerGroup.title;
            } else {
                this.searchParams.organizer_group_id = null;
                this.dummyEvent.organizer_group_id = null;
                this.searchParams.organizer_group_title = null;
                this.organizerGroupDisplayValue = null;
            }
        },

        onImportProtectionChange(value) {
            this.searchParams.import_protection = value;
        },
    },
};
</script>
